import * as React from 'react'
import { Button, Container, Modal as _Modal, Col, Row, Form } from 'react-bootstrap'
import { css, styled } from 'styled-components'
import * as domain from '@packages/domain'
import { pipe, RR, RD, TE, t, O } from '@packages/prelude'
import { Order } from './Order'
import { Request } from './Request'
import { Checkbox } from './Checkbox'
import * as order from '~/api/order'
import { generateConfigurationData } from '~/commons/configurationData'
import { react } from '~/commons'

const Modal = styled(_Modal)`
  z-index: 1020;

  .modal-content {
    padding-top: 6rem;

    background: #102e3e;
    color: var(--bs-white);

    @media screen and (min-width: 768px) {
      padding-top: 10rem;
    }
  }
`

const Navigation = styled.div`
  display: flex;
`

const NavItem = styled.div<{ $isDisabled?: boolean; $isActive?: boolean }>`
  width: calc(100% / 3);

  border-bottom: 2px solid var(--bs-secondary);
  padding-bottom: 1rem;

  text-transform: uppercase;
  font-family: var(--bs-font-sans-serif);
  font-size: 0.875rem;
  color: var(--bs-secondary);
  font-weight: bold;

  opacity: 0.4;
  cursor: pointer;

  @media screen and (min-width: 768px) {
    border-bottom: 4px solid var(--bs-secondary);
    font-size: 1.125rem;
  }

  ${props =>
    props.$isActive === true &&
    css`
      opacity: 1;
      pointer-events: none;
    `}

  ${props =>
    props.$isDisabled === true &&
    css`
      pointer-events: none;
    `}
`

const Label = styled(Form.Label)`
  font-family: var(--bs-font-sans-serif);
  font-size: 0.875rem;
  text-transform: uppercase;
  font-weight: bold;
  color: var(--bs-info);
`

const Input = styled(Form.Control)`
  background-color: var(--bs-white) !important;
  color: var(--bs-primary);
  border: 1px solid var(--bs-secondary);

  @media screen and (min-width: 768px) {
    border-width: 2px;
    width: 50%;
  }
`

const PaymentSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: center;
`

const CountdownCircle = styled.div`
  width: 80px;
  height: 80px;
  border: 4px solid var(--bs-secondary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem auto;
  position: relative;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--bs-white);

  &::before {
    content: '';
    position: absolute;
    width: 88px;
    height: 88px;
    border: 4px solid transparent;
    border-top: 4px solid var(--bs-info);
    border-radius: 50%;
    top: -8px;
    left: -8px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

interface PurchaseFormProps {
  show: boolean
  onHide: () => void
  selectedOptions: RR.ReadonlyRecord<string, ReadonlyArray<domain.Option.Type>>
  configuration: domain.Configurator.Type
  productId: number
  price: number
}

const SubmissionTypeOrder = t.literal('order')
const SubmissionTypeRequest = t.literal('request')

export const SubmissionType = t.union([SubmissionTypeOrder, SubmissionTypeRequest])

export type SubmissionType = t.TypeOf<typeof SubmissionType>

export interface SubmissionResult {
  message: string
  type: SubmissionType
  orderId?: number
}

interface SubmissionOrderExtra {
  paymentType: domain.PaymentType.Type
}

interface SubmissionRequestExtra {
  request: string
}

type SubmissionExtra = SubmissionOrderExtra | SubmissionRequestExtra

type ViewState = 'done' | 'form'

// Form View Component
interface PurchaseFormViewProps {
  submissionState: RD.RemoteData<Error, SubmissionResult>
  validated: boolean
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  price: number
}

const PurchaseFormView: React.FC<PurchaseFormViewProps> = ({ submissionState, validated, onSubmit, price }) => {
  const [submissionType, setSubmissionType] = React.useState<O.Option<SubmissionType>>(O.none)

  const handleBack = () => {
    setSubmissionType(O.none)
  }

  return (
    <Container className="pt-md-3" fluid>
      <Row>
        <Col xs={12} md={3}>
          <h1 className="text-white">Ihre Daten*</h1>
          <p>* Ihre Daten nutzen wir ausschließlich zu Zwecken der Anfrage- bzw. Auftragsbearbeitung. </p>
          <a className="text-white" href="/datenschutz">
            Zur Datenschutzerklärung
          </a>
        </Col>
        <Col xs={12} md={{ span: 8, offset: 1 }} className="mt-4 mt-md-0">
          <Form noValidate validated={validated} onSubmit={onSubmit}>
            <Form.Group controlId="purchaseForm.email" className="mb-5">
              <Label>E-Mail-Adresse</Label>
              <Input type="email" name="email" placeholder="name@beispiel.de" required />
            </Form.Group>
            <Checkbox
              type="checkbox"
              id="terms"
              required
              label={
                <span>
                  Ich akzeptiere die <a href="/agb">AGB</a> der RelaxTrailer GmbH.
                </span>
              }
              className="my-4"
            />
            <Checkbox
              type="checkbox"
              id="privacyPolicy"
              required
              label={
                <span>
                  Ich habe die <a href="/datenschutz">Datenschutzerklärung</a> gelesen und stimme zu.
                </span>
              }
              className="my-4"
            />

            {O.isNone(submissionType) && (
              <div className="d-flex gap-4">
                <Button
                  variant="secondary"
                  className="mt-5"
                  type="button"
                  onClick={() => setSubmissionType(O.some('order'))}
                  disabled={RD.isPending(submissionState) || RD.isSuccess(submissionState)}>
                  Kaufen
                </Button>
                <Button
                  variant="secondary"
                  className="mt-5"
                  type="button"
                  onClick={() => setSubmissionType(O.some('request'))}
                  disabled={RD.isPending(submissionState) || RD.isSuccess(submissionState)}>
                  Anfragen
                </Button>
              </div>
            )}

            {pipe(
              submissionType,
              O.foldMap(react.monoid)(type =>
                SubmissionTypeOrder.is(type) ? (
                  <Order submissionState={submissionState} handleBack={handleBack} price={price} />
                ) : (
                  <Request submissionState={submissionState} handleBack={handleBack} />
                )
              )
            )}
          </Form>
        </Col>
      </Row>
    </Container>
  )
}

// Done View Component
interface PurchaseFormDoneViewProps {
  submissionState: RD.RemoteData<Error, SubmissionResult>
}

// set to 1000000 if environment is development
const REDIRECTING_TIMEOUT = process.env.NODE_ENV === 'development' ? 1000000 : 10000

const PurchaseFormDoneView: React.FC<PurchaseFormDoneViewProps> = ({ submissionState }) => {
  const [countdown, setCountdown] = React.useState(REDIRECTING_TIMEOUT / 1000)
  const [isRedirecting, setIsRedirecting] = React.useState(false)

  // Get order result if available
  const orderResult = pipe(submissionState, RD.toOption)

  // Countdown timer for orders
  React.useEffect(() => {
    pipe(
      orderResult,
      O.chainFirst(O.fromPredicate(result => result.type === 'order' && !isRedirecting)),
      O.chainEitherK(result => pipe(result?.orderId, t.number.decode)),
      O.map(orderId => {
        const timer = setTimeout(() => {
          redirectToPayment(orderId)
        }, REDIRECTING_TIMEOUT)
        return () => clearTimeout(timer)
      })
    )
  }, [orderResult, isRedirecting])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCountdown(countdown - 1)
    }, 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const redirectToPayment = (orderId: number) => {
    setIsRedirecting(true)
    const paymentUrl = `/product-order/${orderId}/payment`
    window.location.href = paymentUrl
  }

  const handlePaymentClick = () => {
    pipe(
      orderResult,
      O.chainEitherK(result => pipe(result?.orderId, t.number.decode)),
      O.match(() => console.error('HandlePaymentClick: no order id'), redirectToPayment)
    )
  }

  return (
    <Container className="my-5 pt-md-5" fluid>
      <Row>
        <Col xs={12} md={3}>
          <h1 className="text-white">
            {pipe(
              submissionState,
              RD.fold(
                () => '',
                () => 'Sende...',
                () => 'Fehler!',
                result => (result.type === 'order' ? 'Fast geschafft!' : 'Viel Spaß!')
              )
            )}
          </h1>
        </Col>
        <Col xs={12} md={{ span: 8, offset: 1 }} className="mt-4 mt-md-0">
          {pipe(
            submissionState,
            RD.fold(
              () => null,
              () => <p>Anfrage wird gesendet...</p>,
              error => (
                <p>
                  Etwas ist beim Absenden fehlgeschlagen: {error.message}
                  <br />
                  Bitte wenden Sie sich telefonisch oder per E-Mail an uns.
                </p>
              ),
              result => (
                <>
                  <p>
                    {result.message}
                    <br />
                    Sie erhalten in Kürze eine E-Mail von uns mit Ihrer Konfiguration als PDF.
                  </p>

                  {result.type === 'order' && (
                    <PaymentSection>
                      <h3 className="mb-3 text-secondary">Jetzt bezahlen</h3>
                      <CountdownCircle>{isRedirecting ? '...' : countdown}</CountdownCircle>
                      <p className="mb-3">Sie werden in Kürze zum Zahlungsdienstleister weitergeleitet</p>
                      <div>
                        <Button variant="info" size="lg" onClick={handlePaymentClick} disabled={isRedirecting}>
                          {isRedirecting ? 'Weiterleitung...' : 'Jetzt bezahlen'}
                        </Button>
                      </div>
                    </PaymentSection>
                  )}
                </>
              )
            )
          )}
          <p>Ihr RelaxTrailer Team</p>
        </Col>
      </Row>
    </Container>
  )
}

export const PurchaseForm: React.FC<PurchaseFormProps> = ({ show, onHide, selectedOptions, configuration, productId, price }) => {
  const [submissionState, setSubmissionState] = React.useState<RD.RemoteData<Error, SubmissionResult>>(RD.initial)
  const [currentView, setCurrentView] = React.useState<ViewState>('form')
  const [validated, setValidated] = React.useState(false)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    pipe(
      // Start with the form element
      TE.of({ form: event.currentTarget }),
      TE.chainFirstIOK(() => () => setValidated(true)),
      // Validate form and handle side effects
      TE.chainFirst(
        TE.fromPredicate(
          ({ form }) => form.checkValidity(),
          () => new Error('Form validation failed', { cause: 'FORM_VALIDATION_FAILED' })
        )
      ),
      // Side effect: Set pending state
      TE.chainFirstIOK(() => () => setSubmissionState(RD.pending)),
      // Extract form data
      TE.bind('formData', ({ form }) => TE.of(new FormData(form))),
      // Validate email
      TE.bind('email', ({ formData }) =>
        pipe(
          formData.get('email'),
          t.NonEmptyString.decode,
          TE.fromEither,
          TE.mapLeft(() => new Error('Email is required'))
        )
      ),
      TE.bind('submissionType', ({ formData }) =>
        pipe(
          formData.get('submissionType'),
          SubmissionType.decode,
          TE.fromEither,
          TE.mapLeft(() => new Error('Submission type is required'))
        )
      ),
      TE.bind('submissionExtra', ({ formData, submissionType }): TE.TaskEither<Error, SubmissionExtra> => {
        switch (submissionType) {
          case 'order':
            return pipe(
              TE.Do,
              TE.apS(
                'paymentType',
                pipe(
                  formData.get('payment_type'),
                  domain.PaymentType.Type.decode,
                  TE.fromEither,
                  TE.mapLeft(() => new Error('Payment type is required'))
                )
              )
            )
          case 'request':
            return pipe(
              TE.Do,
              TE.apS(
                'request',
                pipe(
                  formData.get('request'),
                  t.NonEmptyString.decode,
                  TE.fromEither,
                  TE.mapLeft(() => new Error('Request is required'))
                )
              )
            )
        }
      }),
      TE.apS('configurationData', TE.of(generateConfigurationData(selectedOptions, price, configuration.id, productId))),
      // Make API call
      TE.chain(({ email, configurationData, submissionType, submissionExtra }) => {
        return order.createOrder({
          email,
          configurationData,
          submissionType,
          configuration: {
            id: configuration.id,
            title: configuration.title
          },
          productId,
          ...submissionExtra
        })
      }),
      // Handle result
      TE.match(
        error => {
          if (error?.cause === 'FORM_VALIDATION_FAILED') {
            // Don't set error state for validation failures - UI already shows validation feedback
            return
          }
          setSubmissionState(RD.failure(error))
          setCurrentView('done')
        },
        ({ realId: orderId, submissionType }) => {
          setSubmissionState(
            RD.success({
              message:
                submissionType === 'order' ? 'Ihre Bestellung wurde erfolgreich übermittelt.' : 'Vielen Dank für Ihre Anfrage.',
              type: submissionType,
              orderId
            })
          )
          setCurrentView('done')
        }
      )
    )()
  }

  return (
    <Modal show={show} onHide={onHide} fullscreen className="purchase-form" backdrop={false}>
      <Modal.Header>
        <Container fluid>
          <Navigation>
            <NavItem onClick={onHide}>Produkt</NavItem>
            <NavItem
              $isActive={currentView === 'form'}
              onClick={() => setCurrentView('form')}
              $isDisabled={RD.isSuccess(submissionState)}>
              Warenkorb
            </NavItem>
            <NavItem
              $isActive={currentView === 'done'}
              $isDisabled={RD.isInitial(submissionState)}
              onClick={() => setCurrentView('done')}>
              {pipe(
                submissionState,
                RD.fold(
                  () => '',
                  () => 'Senden…',
                  () => 'Ups',
                  () => 'Hurra'
                )
              )}
            </NavItem>
          </Navigation>
        </Container>
      </Modal.Header>

      <Modal.Body>
        {currentView === 'form' && (
          <PurchaseFormView submissionState={submissionState} validated={validated} onSubmit={handleSubmit} price={price} />
        )}
        {currentView === 'done' && <PurchaseFormDoneView submissionState={submissionState} />}
      </Modal.Body>
    </Modal>
  )
}
